import { ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { environment } from 'environments/environment';
import { UserService } from 'app/services/user.service';
import { LoadingService } from 'app/services/loading.service';
import { ScriptService } from 'app/services/ScriptService';
import { UtilityService } from './services/utility.service';
import {
	NavigationCancel,
	NavigationEnd,
	NavigationError,
	NavigationStart,
	Router,
	ActivatedRoute,
} from '@angular/router';
import { datadogRum } from '@datadog/browser-rum';
import { RumActionTrackerService } from 'app/services/rum-action-tracker.service';
import { SessionExpiryWarningService } from 'app/services/session-expiry-warning.service';
import { getRumViewName } from 'app/services/rum-view-name.util';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
	selector: 'app-root',
	templateUrl: './app.component.html',
	styleUrls: ['./app.component.css'],
	standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
	loading = false;
	loadingChanges = [];
	private readonly destroy$ = new Subject<void>();
	private readonly storageListener = (event: StorageEvent) => this.onStorageClientChange(event);

	constructor(
		private loadingService: LoadingService,
		private cdref: ChangeDetectorRef,
		private userService: UserService,
		private scriptService: ScriptService,
		private utilityService: UtilityService,
		public router: Router,
		private activatedRoute: ActivatedRoute,
		private rumActionTracker: RumActionTrackerService,
		private sessionExpiryWarning: SessionExpiryWarningService
	) {
		if (userService.currentUserValue?.user) {
			let clientId = localStorage.getItem(UserService.clientIdCookie);
			if (!clientId) {
				clientId = userService.currentUserValue.user.clientId;
			}

			if (clientId) {
				this.userService.setClientId(clientId);
				this.utilityService.retrieveConfiguration(clientId, null, false);
			}
			userService.setDatadogRUMUser();
		}
		window.addEventListener('storage', this.storageListener);
	}

	ngOnInit() {
		this.rumActionTracker.init();
		this.sessionExpiryWarning.start();

		if (environment.production || environment.name === 'UAT') {
			this.scriptService
				.load('hs-script-loader')
				.then((_) => {})
				.catch((error) => console.error('Failed to load hs-script-loader:', error));
		}

		this.router.events.pipe(takeUntil(this.destroy$)).subscribe((event) => {
			if (event instanceof NavigationStart) {
				this.loadingService.OnLoading.emit(true);
			} else if (
				event instanceof NavigationEnd ||
				event instanceof NavigationCancel ||
				event instanceof NavigationError
			) {
				if (event instanceof NavigationEnd) {
					const explicitViewName = this.getRouteViewName(this.activatedRoute);
					const viewName = explicitViewName || getRumViewName(event.urlAfterRedirects ?? event.url);
					datadogRum.startView({ name: viewName });
				}
				this.loadingService.OnLoading.emit(false);
			}
		});

		this.loadingService.OnLoading.pipe(takeUntil(this.destroy$)).subscribe((data: boolean) => {
			this.updateLoadingState(data);
		});

		this.userService.validateMappings();
		if (!localStorage.getItem(UserService.mappingName)) {
			this.userService.getFormMappingAll().subscribe((data) => {
				this.userService.setFormMappings(data);
			});
		}
		this.loadLazyScripts();
	}

	private getRouteViewName(route: ActivatedRoute): string | undefined {
		let current: ActivatedRoute | null = route;
		while (current?.firstChild) {
			current = current.firstChild;
		}
		return current?.snapshot?.data?.['viewName'] as string | undefined;
	}

	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
		window.removeEventListener('storage', this.storageListener);
	}

	resetLoading() {
		this.loadingChanges = [];
		this.loading = false;
		try {
			if (this.cdref) {
				this.cdref.detectChanges();
			}
		} catch (e) {}
	}

	startTimer() {
		setTimeout(this.resetLoading, 10000);
	}

	updateLoadingState(data: boolean) {
		let needsChange = false;
		if (this.loadingChanges.length == 0 && data) {
			needsChange = true;
		}
		if (data) {
			this.loadingChanges.push(data);
		} else {
			if (this.loadingChanges.length > 0) {
				this.loadingChanges.pop();
			}
		}

		if (this.loadingChanges.length == 0 && !data) {
			needsChange = true;
		}

		if (this.loading != data && needsChange) {
			this.loading = data;
			this.cdref.detectChanges();
		}
	}

	@HostListener('mousewheel', ['$event']) onMouseWheelChrome(event: any) {
		this.disableScroll(event);
	}

	@HostListener('DOMMouseScroll', ['$event']) onMouseWheelFirefox(event: any) {
		this.disableScroll(event);
	}

	@HostListener('onmousewheel', ['$event']) onMouseWheelIE(event: any) {
		this.disableScroll(event);
	}

	disableScroll(event: any) {
		if (event.srcElement.type === 'number') {
			event.preventDefault();
		}
	}

	loadLazyScripts() {
		this.scriptService
			.load('pdfmake-pdfmake')
			.then((_) => {})
			.catch((error) => console.error('Failed to load pdfmake:', error));
		this.scriptService
			.load('pdfmake-vfs_fonts')
			.then((_) => {})
			.catch((error) => console.error('Failed to load pdfmake-vfs_fonts:', error));
		this.scriptService
			.load('exceljs')
			.then((_) => {})
			.catch((error) => console.error('Failed to load exceljs:', error));
	}

	onStorageClientChange(event: StorageEvent) {
		if (event.key === UserService.clientIdCookie && event.newValue) {
			location.reload();
		}
	}
}
